import React, { FC, useState } from 'react';
import { Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LoadingSpinner from '../components/loadingSpinner/loadingSpinner';
import PauseButton from '../assets/icons/pause-icon.svg';
import PlayButton from '../assets/icons/play-icon.svg';
import DisabledPauseButton from '../assets/icons/disabled-pause-icon.svg';
import closableEnqueueSnackbar from '../components/closableEnqueueSnackbar/closableEnqueueSnackbar';
import { useStartAdMutation, useStopAdMutation } from '../apiHooks/mutations/adManagementMutations/startOrStopAdMutation';
import { TrackingSourceEnum } from '../components/enums/trackingSourceEnum';

interface PlayStopAdCellProps {
  params?: boolean;
  rowObject?: any;
}

const PlayStopAdCell: FC<PlayStopAdCellProps> = (props) => {
  const { t } = useTranslation();
  const [isStopped, setIsStopped] = useState(!props.params);
  const [isProcessing, setIsProcessing] = useState(false);

  const onStartSuccess = () => {
    closableEnqueueSnackbar(t('adStartSuccess'), 'success');
  };

  const onStopSuccess = () => {
    closableEnqueueSnackbar(t('adStopSuccess'), 'success');
  };

  const {
    mutateAsync: startAd,
  } = useStartAdMutation(props.rowObject.trackingSourceId, onStartSuccess);

  const {
    mutateAsync: stopAd,
  } = useStopAdMutation(props.rowObject.trackingSourceId, onStopSuccess);

  const handleStartAd = async () => {
    if (!props.rowObject?.adId || isProcessing) return;
    setIsProcessing(true);

    setIsStopped(false);

    try {
      await startAd(String(props.rowObject.adId));
    } catch (error) {
      setIsStopped(true);
      closableEnqueueSnackbar(t('adStartError'), 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleStopAd = async () => {
    if (!props.rowObject?.adId || isProcessing) return;
    setIsProcessing(true);

    setIsStopped(true);

    try {
      await stopAd(String(props.rowObject.adId));
    } catch (error) {
      setIsStopped(false);
      closableEnqueueSnackbar(t('adStopError'), 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  if (
      props.rowObject.trackingSourceId !== TrackingSourceEnum.Google &&
      props.rowObject.trackingSourceId !== TrackingSourceEnum.Facebook
  ) {
    return (
        <button
            type="button"
            disabled
            className="grid h-[30px] w-[30px] cursor-not-allowed place-content-center rounded-md border border-dark bg-white"
        >
          <Tooltip arrow placement="top" title={t('cannotBeControlled')} className="h-full w-full p-1.5">
            <img src={DisabledPauseButton} alt="disabled pause button icon" className="h-3 w-3 object-contain" />
          </Tooltip>
        </button>
    );
  }

  return (
      <button
          type="button"
          onClick={isStopped ? handleStartAd : handleStopAd}
          disabled={isProcessing}
          className={`grid h-[30px] w-[30px] place-content-center rounded-md border border-dark bg-white duration-150
        hover:border-primary/50 active:border-primary ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {isProcessing ? (
            <LoadingSpinner size={12} color="primary" center />
        ) : (
            <Tooltip
                arrow
                placement="top"
                title={isStopped ? t('startAd') : t('stopAd')}
                className="h-full w-full p-2"
            >
              <img
                  src={isStopped ? PlayButton : PauseButton}
                  alt={isStopped ? 'play button icon' : 'pause button icon'}
                  className="h-3 w-3 object-contain"
              />
            </Tooltip>
        )}
      </button>
  );
};

export default PlayStopAdCell;
